import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Settings, X } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { cn } from '@/lib/utils'

export function CookieConsent() {
  const { t } = useTranslation('common')
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      {/* Banner */}
      <AnimatePresence>
        {!showPreferences && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className={cn(
              'fixed bottom-4 left-4 z-[9999] max-w-sm',
              'rounded-xl bg-secondary-900 p-5 shadow-2xl',
              'border border-secondary-700'
            )}
            role="dialog"
            aria-label={t('cookie.title')}
          >
            <p className="mb-4 pr-2 text-sm leading-relaxed text-secondary-300">
              {t('cookie.message')}{' '}
              <LocalizedLink
                to="privacy"
                className="font-medium text-primary-400 underline underline-offset-2 transition-colors hover:text-primary-300"
              >
                {t('cookie.learnMore')}
              </LocalizedLink>
            </p>

            <div className="flex gap-2">
              <button
                onClick={acceptAll}
                className={cn(
                  'flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold',
                  'bg-primary-400 text-ink transition-colors',
                  'hover:bg-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-secondary-900'
                )}
              >
                {t('cookie.acceptAll')}
              </button>
              <button
                onClick={rejectAll}
                className={cn(
                  'flex-1 rounded-lg border-2 border-secondary-500 px-4 py-2.5 text-sm font-semibold',
                  'bg-transparent text-secondary-300 transition-colors',
                  'hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-secondary-400 focus:ring-offset-2 focus:ring-offset-secondary-900'
                )}
              >
                {t('cookie.rejectAll')}
              </button>
            </div>

            <button
              onClick={() => setShowPreferences(true)}
              className="mt-3 flex w-full items-center justify-center gap-1.5 text-xs text-secondary-400 transition-colors hover:text-secondary-200"
            >
              <Settings className="h-3.5 w-3.5" />
              {t('cookie.customize')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preferences Modal */}
      <AnimatePresence>
        {showPreferences && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4"
            onClick={() => setShowPreferences(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-secondary-700 bg-secondary-900 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">
                    {t('cookie.preferencesTitle')}
                  </h2>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="rounded-md p-1 text-secondary-400 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                    aria-label={t('cookie.closePreferences')}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <p className="mb-6 text-sm text-secondary-400">
                  {t('cookie.preferencesDescription')}
                </p>

                <div className="space-y-3">
                  {/* Necessary */}
                  <div className="flex items-center justify-between rounded-lg bg-secondary-800 p-3">
                    <div>
                      <p className="text-sm font-medium text-white">{t('cookie.necessary')}</p>
                      <p className="text-xs text-secondary-400">{t('cookie.necessaryDesc')}</p>
                    </div>
                    <span className="text-xs font-medium text-primary-400">{t('cookie.alwaysOn')}</span>
                  </div>

                  {/* Analytics */}
                  <label className="flex cursor-pointer items-center justify-between rounded-lg bg-secondary-800 p-3 transition-colors hover:bg-secondary-700">
                    <div>
                      <p className="text-sm font-medium text-white">{t('cookie.analytics')}</p>
                      <p className="text-xs text-secondary-400">{t('cookie.analyticsDesc')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={tempPrefs.analytics}
                      onChange={(e) => setTempPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                      className="h-5 w-5 cursor-pointer rounded border-secondary-600 accent-primary-500"
                    />
                  </label>

                  {/* Marketing */}
                  <label className="flex cursor-pointer items-center justify-between rounded-lg bg-secondary-800 p-3 transition-colors hover:bg-secondary-700">
                    <div>
                      <p className="text-sm font-medium text-white">{t('cookie.marketing')}</p>
                      <p className="text-xs text-secondary-400">{t('cookie.marketingDesc')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={tempPrefs.marketing}
                      onChange={(e) => setTempPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                      className="h-5 w-5 cursor-pointer rounded border-secondary-600 accent-primary-500"
                    />
                  </label>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-secondary-400 transition-colors hover:bg-secondary-800 hover:text-white"
                  >
                    {t('cookie.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      savePreferences(tempPrefs)
                      setShowPreferences(false)
                    }}
                    className={cn(
                      'rounded-lg px-4 py-2 text-sm font-semibold',
                      'bg-primary-400 text-ink transition-colors',
                      'hover:bg-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-400'
                    )}
                  >
                    {t('cookie.savePreferences')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
